import {
  type Context,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { isNonNullable } from '@grammarly/function-utils'
import type {
  UseVBarStackItemParameters,
  VBarStackClusterItem,
  VBarStackItem,
  VBarStackProps,
} from '../types'
import type { VBarStackContext } from './VBarStackContext'
import {
  layoutVBarItems,
  type VBarClusterLayout,
  type VBarItem,
  type VBarItemLayout,
} from './layoutVBarItems'
import type { NonEmptyArray } from '../utils/types'
import { roundToFirstDecimalPlace } from '../utils/roundToFirstDecimalPlace'
import { useDelayedCallback } from '../utils/useDelayedCallback'
import { useStateUpdateOnAnimationFrame } from '../utils/useStateUpdateOnAnimationFrame'

/** This should only hold data that affects layout */
interface VBarItemForLayout extends VBarItem {
  readonly active: boolean
}

interface VBarItemState<ClusterItemMeta> {
  readonly item?: VBarStackItem
  readonly clusterId?: string
  readonly clusterItemData?: ClusterItemMeta
  readonly onStateChanged: (state: VBarStackItem) => void
}

interface LayoutInput {
  /** The items data and whether they were expanded by the user */
  readonly items: ReadonlyMap<string, VBarItemForLayout>
  /**
   * The set of clusters that are expanded by hover (before the containing items are hovered)
   * This is a transient state that allows to expand clusters before their items are hovered and marked as active.
   * When a first item is marked as active, the cluster will be removed from that set so that it can be collapsed
   * when no items are active anymore.
   */
  readonly expandedClusters: ReadonlySet<string>
}

export function VBarStack<ClusterItemMeta>({
  VBarStackContext,
  VBarCluster,
  clusterThreshold = 0,
  interClusterGap = 0,
  expandedClusterBottomMargin = 0,
  clusterCollapseDelayMs = 0,
  collapsedClusterMinContentHeight,
  containerScrollHeight,
  children,
}: PropsWithChildren<
  VBarStackProps<ClusterItemMeta> & {
    readonly VBarStackContext: Context<VBarStackContext<ClusterItemMeta>>
  }
>) {
  // Input to the layout algorithm - The list of items to be positioned and the set of expanded clusters.
  const [layoutInput, setLayoutInput] = useStateUpdateOnAnimationFrame<LayoutInput>({
    items: new Map(),
    expandedClusters: new Set(),
  })

  // List of collapsed clusters to render.
  const [clusters, setClusters] = useState<VBarStackClusterItem<ClusterItemMeta>[]>([])

  // Mutable item state. This holds the onChange handler, the cluster metadata, and the current item state.
  const itemsState = useMemo(() => new Map<string, VBarItemState<ClusterItemMeta>>(), [])
  const expandedClusterIdsToVBarIds = useMemo(() => new Map<string, Set<string>>(), [])

  const collapseClusterIfNoChildrenActive = useCallback(
    (clusterId: string) => {
      setLayoutInput(state => {
        const children = expandedClusterIdsToVBarIds.get(clusterId) ?? new Set<string>()
        const hasActiveChildren = [...children].some(id => state.items.get(id)?.active)
        if (hasActiveChildren) {
          return state
        }
        const newExpanded = new Set(state.expandedClusters)
        if (newExpanded.has(clusterId)) {
          newExpanded.delete(clusterId)
          return { ...state, expandedClusters: newExpanded }
        }
        return state
      })
    },
    [expandedClusterIdsToVBarIds, setLayoutInput],
  )

  const scheduleClusterCollapseIfNoChildrenActive = useDelayedCallback(
    collapseClusterIfNoChildrenActive,
    clusterCollapseDelayMs,
  )

  // Perform layout, store the new item state and notify children of their new state.
  useEffect(() => {
    const activeVBars = new Set<string>(
      [...layoutInput.items.values()].filter(i => i.active).map(i => i.id),
    )
    // For all active VBars, we find their calculated top position and try to preserve it
    // This avoids jumps of the active VBar under the cursor if a cluster gets collapsed above it.
    const stableVBarsTops = new Map(
      [...itemsState.entries()]
        .filter(([id, _]) => activeVBars.has(id))
        .map(([id, state]) =>
          state.item?.kind === 'expanded' ? ([id, state.item.top] as const) : null,
        )
        .filter(isNonNullable),
    )
    const clusters = layoutVBarItems({
      orderedItems: [...layoutInput.items.values()].sort((a, b) => a.anchor.top - b.anchor.top),
      collapsedClusterMinContentHeight,
      scrollHeight: containerScrollHeight,
      clusterThreshold,
      interClusterGap,
      expandedClusterBottomMargin,
      expandedVBars: activeVBars,
      expandedClusters: layoutInput.expandedClusters,
      stableVBarsTops,
    })

    // Store the new item state and notify children of their new state.
    updateItemsState(clusters, itemsState)

    // Update the list of collapsed clusters
    const collapsedClusters: VBarStackClusterItem<ClusterItemMeta>[] = getCollapsedClusters(
      clusters,
      itemsState,
      id => {
        // Trigger another layout with this cluster expanded
        setLayoutInput(state => {
          const newExpanded = new Set(state.expandedClusters)
          if (!newExpanded.has(id)) {
            newExpanded.add(id)
            return { ...state, expandedClusters: newExpanded }
          }
          return state
        })

        // And schedule a collapse of the cluster if no children are active after a delay
        scheduleClusterCollapseIfNoChildrenActive(id)
      },
    )

    expandedClusterIdsToVBarIds.clear()
    for (const cluster of clusters) {
      if (cluster.expanded && cluster.items.length > 1) {
        // Update the expanded cluster IDs to VBar IDs map
        const vbarIds = cluster.items.map(item => item.id)
        expandedClusterIdsToVBarIds.set(cluster.id, new Set(vbarIds))
      }
    }

    // Re-render the collapsed clusters
    setClusters(collapsedClusters)
  }, [
    layoutInput,
    itemsState,
    collapsedClusterMinContentHeight,
    interClusterGap,
    expandedClusterBottomMargin,
    clusterThreshold,
    scheduleClusterCollapseIfNoChildrenActive,
    expandedClusterIdsToVBarIds,
    containerScrollHeight,
    setLayoutInput,
  ])

  const upsert = useCallback<VBarStackContext<ClusterItemMeta>['upsert']>(
    itemUpsert => {
      const itemState = itemsState.get(itemUpsert.id)
      itemsState.set(itemUpsert.id, {
        ...(itemState ?? {}),
        clusterItemData: itemUpsert.clusterItemData,
        onStateChanged: itemUpsert.onStateChanged,
      })

      // Trigger another layout
      setLayoutInput(
        updateLayoutItemWithUpsertParams(
          itemsState,
          itemUpsert,
          scheduleClusterCollapseIfNoChildrenActive,
        ),
      )
    },
    [itemsState, scheduleClusterCollapseIfNoChildrenActive, setLayoutInput],
  )

  const remove = useCallback<VBarStackContext<ClusterItemMeta>['remove']>(
    id => {
      const item = itemsState.get(id)
      if (item && item.clusterId !== undefined) {
        const cluster = expandedClusterIdsToVBarIds.get(item.clusterId)
        if (cluster && cluster.has(id)) {
          cluster.delete(id)
          collapseClusterIfNoChildrenActive(item.clusterId)
        }
      }

      itemsState.delete(id)

      // Trigger another layout
      setLayoutInput(state => {
        const newItems = new Map(state.items)
        const removed = newItems.delete(id)
        return removed ? { ...state, items: newItems } : state
      })
    },
    [itemsState, collapseClusterIfNoChildrenActive, expandedClusterIdsToVBarIds, setLayoutInput],
  )

  const contextValue = useMemo<VBarStackContext<ClusterItemMeta>>(
    () => ({ upsert, remove, isConnected: true }),
    [upsert, remove],
  )

  return (
    <VBarStackContext.Provider value={contextValue}>
      {children}
      {clusters.map(cluster => {
        return <VBarCluster key={cluster.id} cluster={cluster} />
      })}
    </VBarStackContext.Provider>
  )
}

function vbarStackItemsEqual(a: VBarStackItem, b: VBarStackItem): boolean {
  return (
    a.kind === b.kind &&
    a.top === b.top &&
    a.anchorHeight === b.anchorHeight &&
    a.maxHeight === b.maxHeight &&
    a.clusterOffset === b.clusterOffset &&
    a.clusterId === b.clusterId
  )
}

/**
 * Update the LayoutInput data with a new item to upsert.
 * This only updates the state if the item data has changed.
 */
function updateLayoutItemWithUpsertParams<ClusterItemMeta>(
  itemsState: ReadonlyMap<string, VBarItemState<ClusterItemMeta>>,
  itemUpsert: UseVBarStackItemParameters<ClusterItemMeta>,
  scheduleClusterCollapseIfNoChildrenActive: (clusterId: string) => void,
): (state: LayoutInput) => LayoutInput {
  return state => {
    const oldItem = state.items.get(itemUpsert.id)

    const newItems = new Map(state.items).set(itemUpsert.id, {
      id: itemUpsert.id,
      anchor: { top: itemUpsert.top, height: itemUpsert.anchorHeight },
      minContentHeight: itemUpsert.minContentHeight,
      active: itemUpsert.active,
    })

    // If the item is now inactive, schedule a collapse of its cluster if it has no active children.
    if (oldItem && oldItem.active && !itemUpsert.active) {
      const itemState = itemsState.get(itemUpsert.id)
      if (itemState?.clusterId !== undefined) {
        scheduleClusterCollapseIfNoChildrenActive(itemState.clusterId)
      }
    }

    return { expandedClusters: state.expandedClusters, items: newItems }
  }
}

/**
 * Updates the `itemsState` map with the new state of each item in the clusters.
 * This will also call the `onStateChanged` callback for each item if its state has changed.
 */
function updateItemsState<ClusterItemMeta>(
  clusters: readonly VBarClusterLayout<VBarItemLayout>[],
  itemsState: Map<string, VBarItemState<ClusterItemMeta>>,
): void {
  // Store the new item state and notify children of their new state.
  for (const cluster of clusters) {
    for (const item of cluster.items) {
      const itemState = itemsState.get(item.id)
      if (!itemState) continue

      const newState: VBarStackItem = {
        kind: cluster.expanded ? 'expanded' : 'collapsed',
        top: item.top,
        clusterOffset: roundToFirstDecimalPlace(item.top - cluster.top),
        anchorHeight: roundToFirstDecimalPlace(
          Math.max(
            0,
            Math.min(item.anchor.top + item.anchor.height - item.top, item.maxContentHeight),
          ),
        ),
        maxHeight: item.maxContentHeight,
        clusterId: cluster.items.length > 1 ? cluster.id : null,
        isConnected: true,
      }

      itemsState.set(item.id, {
        ...itemState,
        item: newState,
        clusterId: cluster.id,
      })

      if (!itemState.item || !vbarStackItemsEqual(itemState.item, newState)) {
        itemState.onStateChanged(newState)
      }
    }
  }
}

/**
 * Get the list of collapsed clusters to render.
 */
function getCollapsedClusters<ClusterItemMeta>(
  clusters: readonly VBarClusterLayout<VBarItemLayout>[],
  itemsState: ReadonlyMap<string, VBarItemState<ClusterItemMeta>>,
  onExpandCluster: (clusterId: string) => void,
): VBarStackClusterItem<ClusterItemMeta>[] {
  return clusters
    .filter(cluster => !cluster.expanded)
    .map(cluster => {
      const itemsMeta = cluster.items
        .map(item => itemsState.get(item.id)?.clusterItemData)
        .filter(isNonNullable)

      const anchorBottom = cluster.items.reduce(
        (bottom, item) => Math.max(item.anchor.top + item.anchor.height, bottom),
        Number.NEGATIVE_INFINITY,
      )
      const anchorHeight = Math.max(
        0,
        Math.min(anchorBottom - cluster.top, cluster.maxContentHeight),
      )

      return itemsMeta.length < 2
        ? null
        : {
            id: cluster.id,
            top: cluster.top,
            maxHeight: cluster.maxContentHeight,
            anchorHeight: roundToFirstDecimalPlace(anchorHeight),
            items: itemsMeta as NonEmptyArray<ClusterItemMeta>,
            onExpand: () => onExpandCluster(cluster.id),
          }
    })
    .filter(isNonNullable)
}
